import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import {ThrowReporter} from 'io-ts/lib/ThrowReporter';
import _, {isString} from 'lodash';

import {makeEnumRuntimeType} from '../api/apiTypes';

/*
 * Runtime types.
 */

export enum InboxesFiltersEnum {
  UNASSIGNED = 'unassigned',
  OPEN = 'open',
  ASSIGNED = 'assigned',
  ARCHIVED = 'archived',
  SNOOZED = 'snoozed',
  TRASHED = 'trashed',
  SPAM = 'spam',
  ALL = 'all',
}

export enum TeammatesMessageFoldersEnum {
  DRAFTS = 'drafts',
  SENT = 'sent',
  OUTBOX = 'outbox',
}

export enum TeammatesFoldersEnum {
  INBOX = 'inbox',
  ASSIGNED = 'assigned',
  ASSIGNED_SHARED = 'assigned-shared',
  SHARED_WITH_ME = 'shared-with-me',
  DISCUSSIONS = 'discussions',
  ALL = 'all',
  TRASHED = 'trashed',
  SPAM = 'spam',
  SENT = 'sent',
}

export enum TeamsFoldersEnum {
  INBOX = 'inbox',
  TRASHED = 'trashed',
  SPAM = 'spam',
}

export enum GlobalFoldersEnum {
  ASSIGNED = 'assigned',
}

export enum InboxesSearchLocationsEnum {
  LOCAL = 'local',
  PINNED_IN_WORKSPACE = 'workspace',
  GLOBAL = 'global',
}

export const RInboxesFilter = makeEnumRuntimeType(InboxesFiltersEnum);
export const RTeammateMessageFolder = makeEnumRuntimeType(TeammatesMessageFoldersEnum);
export const RTeammateFolder = makeEnumRuntimeType(TeammatesFoldersEnum);
export const RTeamFolder = makeEnumRuntimeType(TeamsFoldersEnum);
export const RGlobalFolder = makeEnumRuntimeType(GlobalFoldersEnum);
export const RInboxesSearchLocation = makeEnumRuntimeType(InboxesSearchLocationsEnum);

const RInboxesUrlBase = t.readonly(
  t.partial({
    inboxId: t.Integer,
    teammateInboxId: t.Integer,
    viewId: t.Integer,
    teammateViewId: t.Integer,
    teammateLensId: t.Integer,
    teamLensId: t.Integer,
    topicId: t.Integer,
    activityId: t.Integer,
    activityDate: t.Integer,
    globalFolder: RGlobalFolder,
    filters: t.readonlyArray(RInboxesFilter),
    searchLocation: RInboxesSearchLocation,
    selectedTokens: t.readonlyArray(t.string),
    fromSelectedTokens: t.readonlyArray(t.string),
    searchQuery: t.string,
  }),
);

const RTeammateFolderUrl = t.intersection([
  RInboxesUrlBase,
  t.readonly(
    t.type({
      teammateId: t.Integer,
      teamId: t.undefined,
      folder: t.union([RTeammateMessageFolder, RTeammateFolder]),
    }),
  ),
]);

const RTeamFolderUrl = t.intersection([
  RInboxesUrlBase,
  t.readonly(
    t.type({
      teammateId: t.undefined,
      teamId: t.Integer,
      folder: RTeamFolder,
    }),
  ),
]);

const RNoFolderUrl = t.intersection([
  RInboxesUrlBase,
  t.readonly(
    t.type({
      teammateId: t.undefined,
      teamId: t.undefined,
      folder: t.undefined,
    }),
  ),
]);

const RInboxesUrl = t.union([RTeammateFolderUrl, RTeamFolderUrl, RNoFolderUrl]);

/*
 * Interface.
 */

interface TeammateFolderUrlModel extends t.TypeOf<typeof RTeammateFolderUrl> {}
interface TeamFolderUrlModel extends t.TypeOf<typeof RTeamFolderUrl> {}
interface NoFolderUrlModel extends t.TypeOf<typeof RNoFolderUrl> {}
export type InboxesUrlModel = TeammateFolderUrlModel | TeamFolderUrlModel | NoFolderUrlModel;

/*
 * IO methods.
 */

export function parseInboxesUrl(src: any): InboxesUrlModel {
  // Transform the params that need transforming.
  const parameters = {
    ...src,
    filters: parseFilters(src.filters),
    inboxId: src.inboxId && Number(src.inboxId),
    teammateInboxId: src.teammateInboxId && Number(src.teammateInboxId),
    viewId: src.viewId && Number(src.viewId),
    teammateViewId: src.teammateViewId && Number(src.teammateViewId),
    teammateLensId: src.teammateLensId && Number(src.teammateLensId),
    teamLensId: src.teamLensId && Number(src.teamLensId),
    teammateId: src.teammateId && Number(src.teammateId),
    teamId: src.teamId && Number(src.teamId),
    topicId: src.topicId && Number(src.topicId),
    activityId: src.around && Number(src.around),
    activityDate: src.sort && src.sort === 'first' ? 0 : src.date && Number(src.date),
    selectedTokens: parseSelectedTokens(src.selectedTokens),
    fromSelectedTokens: parseSelectedTokens(src.fromSelectedTokens),
    searchQuery: src.searchQuery && decodeURIComponent(src.searchQuery),
  };

  // Validate the model.
  const modelValidated = RInboxesUrl.decode(parameters);
  if (!E.isRight(modelValidated)) {
    ThrowReporter.report(modelValidated);
    throw new Error('[io-ts] This error should not be reached.');
  }

  // We need at least one ID for the model to be valid.
  const result = modelValidated.right;
  if (
    !result.inboxId &&
    !result.teammateInboxId &&
    !result.viewId &&
    !result.teammateViewId &&
    !result.teamLensId &&
    !result.teammateLensId &&
    !result.teammateId &&
    !result.teamId &&
    !result.globalFolder &&
    !result.topicId
  ) {
    throw new Error('The model needs at least one resource ID to be valid.');
  }

  return result;
}

function parseFilters(filters: any) {
  if (!isString(filters)) {
    return [];
  }

  return _(filters.split(','))
    .map((f) => {
      const parsedFilter = RInboxesFilter.decode(f);
      return E.isRight(parsedFilter) && parsedFilter.right;
    })
    .compact()
    .value();
}

function parseSelectedTokens(selectedTokens: any) {
  if (!selectedTokens) {
    return undefined;
  }

  return selectedTokens.split(',');
}
